import { debounce } from "@mui/material";
import { useLazyQueryHandler } from "@sinch/hooks/useLazyQueryHandler";
import { useOptionsByValues } from "@sinch/hooks/utilities/useOptionsByValues";
import { mapToOptions, Option } from "@sinch/utils/option";
import { includes } from "ramda";
import { ensureArray, isNotEmpty } from "ramda-adjunct";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AutocompleteInput } from "../../../form/AutocompleteInput/AutocompleteInput";
import { PaymentMethodListItem, PaymentMethodsList } from "./queries.graphql";
import {
  PaymentMethodListItemFragment,
  PaymentMethodsListQuery,
  PaymentMethodsListQueryVariables,
} from "./queries.types";
import { AutocompleteInputProps, AutocompleteOptions } from "@sinch/components/form/AutocompleteInput/types";

export const PaymentMethodsSelect = <
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: Omit<AutocompleteInputProps<AutocompleteOptions, Multiple, DisableClearable, FreeSolo>, "ref" | "options">
) => {
  const { t } = useTranslation();
  const [inputVal, setInputVal] = useState<string>("");

  const { data, dispatch, loading } = useLazyQueryHandler<PaymentMethodsListQuery, PaymentMethodsListQueryVariables>(
    PaymentMethodsList
  );

  const valuesOptions = useOptionsByValues<PaymentMethodListItemFragment>({
    values: ensureArray(props.value ?? []).map(String),
    fragment: PaymentMethodListItem,
  });
  const options: Option[] = useMemo(
    () => mapToOptions("id", "name")([...valuesOptions, ...(data?.paymentMethods || [])]),
    [data?.paymentMethods, valuesOptions]
  );

  const debounceDispatch = useCallback<typeof dispatch>((...args) => debounce(dispatch, 200)(...args), [dispatch]);

  const handleSearch = (search: string) => {
    if (isNotEmpty(search)) {
      debounceDispatch({ name: search });
    }
  };

  return (
    <AutocompleteInput<AutocompleteOptions, Multiple, DisableClearable, FreeSolo>
      noOptionsText={isNotEmpty(inputVal) ? undefined : t("typeToSearch")}
      openOnFocus
      placeholder={t("search")}
      size="small"
      {...props}
      filterSelectedOptions
      inputValue={inputVal ?? ""}
      loading={loading}
      onFocus={() => handleSearch(inputVal)}
      onInputChange={(event, newInputValue, reason) => {
        // prevent backfire on select
        if (!event || (props.multiple && includes(reason, ["removeOption", "selectOption"]))) return;

        setInputVal(newInputValue);
        handleSearch(newInputValue);
      }}
      options={options}
    />
  );
};
